import { Button, Modal } from '@distribusion/owl-frontend-ui'
import { Field, useFormikContext } from 'formik'
import React, { ReactElement } from 'react'
import { useParams } from 'react-router-dom'

import { useUserAuth } from '@hooks/userAuthProvider/provider'
import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import { email, required, chain } from '@lib/validators'
import { useChangeEmail } from '@loaders/changeEmail'
import { AccountFormData } from '@pages/Account/Form/Provider'
import { ModalType, MODAL_TYPES } from '@pages/Account/hooks/useEmailChange'
import InputField from '@ui/Input/Field'

import '@pages/Account/Settings/Modals/ChangeEmailModal/index.scss'

interface ChangeEmailModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (email: string) => void
  onError: (errorType: ModalType) => void
}

export const ChangeEmailModal = ({ isOpen, onClose, onSuccess, onError }: ChangeEmailModalProps): ReactElement => {
  const { t } = useTranslation()
  const { values, errors, touched } = useFormikContext<AccountFormData>()
  const { user } = useUserAuth()
  const { retailerPartnerNumber } = useParams()

  const { mutateAsync: changeEmail } = useChangeEmail()

  const newEmail = values.settings.email.trim()
  const hasValidationError = touched.settings?.email && !!errors.settings?.email
  const isEmailUnchanged = newEmail === user?.email
  const isButtonDisabled = !newEmail || isEmailUnchanged || hasValidationError

  const handleSubmit = async (): Promise<void> => {
    if (!newEmail || !user?.id) /* istanbul ignore next */ return

    try {
      await changeEmail({
        userId: user.id,
        email: newEmail,
        retailerPartnerNumber: Number(retailerPartnerNumber),
      })
      onSuccess(newEmail)
    } catch (error) {
      onError(MODAL_TYPES.CHANGE_EMAIL)
    }
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={t('account.settings.signIn.emailModal.title')}
      maxWidth="sm"
      dataTag="change-email-modal"
    >
      <div className={bem('email-modal', 'content')}>
        <span> {t('account.settings.signIn.emailModal.description')}</span>
        <Field
          component={InputField}
          label={t('account.settings.signIn.email')}
          name="settings.email"
          validate={chain([required, email])}
          required
          dataTag="new-email-input"
        />
        <Button
          color="primary"
          className={bem('email-modal', 'button')}
          onClick={handleSubmit}
          disabled={isButtonDisabled}
          dataTag="submit-email-change"
        >
          {t('account.settings.signIn.emailModal.button')}
        </Button>
      </div>
    </Modal>
  )
}
